import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerLayout from '../../components/layout/CustomerLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { Button } from '../../components/ui/button';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Minus, Trash2, CalendarDays, Clock, ShoppingBag, ArrowLeft, Tag, X, Store, Truck, MapPin, Check, AlertCircle, RefreshCcw, BadgePercent } from 'lucide-react';
import { format, addDays } from 'date-fns';
import axios from 'axios';
import MapAddressPicker from '../../components/customer/MapAddressPicker';
import {
  shouldOfferCustomerWebPush, markCustomerWebPushOffered, enableCustomerWebPush,
} from '../../utils/webPush';
import { cartQuoteKey, priceChangedQuote } from '../../utils/cartPricing';
import { storeHasCoupons } from '../../utils/couponForm';
import { useLoginToContinue } from '../../hooks/useLoginToContinue';
import { isSessionAuthError, takeLoginIntent } from '../../utils/loginIntent';
import { cartItemsNotSubscribable, notSubscribableMessage, storeOffersSubscriptions } from '../../utils/subscriptionEligibility';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'alternate', label: 'Alternate Days' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'twice_weekly', label: 'Twice a Week' },
  { value: 'monthly', label: 'Monthly' },
];

// Razorpay's checkout is entirely their own hosted page — checkout.js injects
// a <div class="razorpay-container"> (position:fixed, top:0, height:100%)
// holding an <iframe class="razorpay-checkout-frame">. Everything visible
// (their header, logo, back button) is cross-origin content inside that
// iframe, which we can never style. It has zero awareness of device safe
// areas since it's a generic web checkout, not built for a native WebView
// shell.
// The iframe itself is `position: relative`, not `fixed` — confirmed by
// inspecting the live DOM. That matters: `top`/`bottom` on a relatively
// positioned element are just an offset from its normal-flow position, not a
// stretch between two edges, so patching the iframe directly (an earlier
// attempt) only nudged it down without shrinking it, pushing the bottom
// (amount/Continue button) off-screen. `.razorpay-container` is the actual
// `position: fixed` box establishing the checkout's on-screen bounds — it's
// same-origin and scriptable, so patching IT properly shrinks the whole
// checkout to fit between the safe-area insets, and the iframe (which fills
// 100% of its parent) follows automatically.
const patchRazorpayFrameSafeArea = () => {
  const applyFix = (container) => {
    container.style.setProperty('top', 'env(safe-area-inset-top, 0px)', 'important');
    container.style.setProperty('bottom', 'env(safe-area-inset-bottom, 0px)', 'important');
    container.style.setProperty('height', 'auto', 'important');
  };
  // A single one-shot patch wasn't enough — Razorpay's own script re-asserts
  // its inline styles asynchronously after checkout first opens (e.g. once
  // its content finishes loading), silently overwriting a fix applied only
  // once. Re-applying on a short interval for as long as the checkout is
  // open/opening makes the fix win no matter when Razorpay's script last
  // touched the element.
  const startedAt = Date.now();
  const tick = () => {
    const container = document.querySelector('.razorpay-container');
    if (container) {
      applyFix(container);
      if (Date.now() - startedAt < 20000) setTimeout(tick, 250);
    } else if (Date.now() - startedAt < 15000) {
      setTimeout(tick, 100); // not injected yet — keep polling briefly
    }
  };
  tick();
};

const CustomerCart = () => {
  const navigate = useNavigate();
  const { user, storeSettings } = useAuth();
  const { items, updateQuantity, removeItem, clearCart, repriceItems, totalAmount, totalSavings, deliverySlot, setDeliverySlot, deliveryDate, setDeliveryDate } = useCart();
  
  const [deliverySlots, setDeliverySlots] = useState([]);
  const [paymentMode, setPaymentMode] = useState('cash');
  const [fulfillmentType, setFulfillmentType] = useState('delivery');
  const [loading, setLoading] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [pickupBranches, setPickupBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [deliveryAddresses, setDeliveryAddresses] = useState([]);
  const [selectedDeliveryAddr, setSelectedDeliveryAddr] = useState(null);
  const [showCartMapPicker, setShowCartMapPicker] = useState(false);
  // Address being added when the session expired, restored after logging in.
  const [cartDraftAddress, setCartDraftAddress] = useState(null);
  const loginToContinue = useLoginToContinue();
  useEffect(() => {
    const intent = takeLoginIntent('/cart');
    if (intent?.action === 'add-address') {
      setCartDraftAddress(intent.draft || null);
      setShowCartMapPicker(true);
    }
  }, []);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [notServiceable, setNotServiceable] = useState(false);
  const [serviceMessage, setServiceMessage] = useState(null);
  const [distanceKm, setDistanceKm] = useState(null);
  const [minOrderVal, setMinOrderVal] = useState(null);
  const [freeDeliveryEligible, setFreeDeliveryEligible] = useState(false);

  // Serviceable Apartments — separate from the distance/branch-based
  // notServiceable check above; gates on PIN code + apartment selection instead.
  const isApartmentsEnabled = storeSettings?.tenant_features?.serviceable_apartments === true;
  const [apartments, setApartments] = useState([]);

  // Subscription state
  const isSubscriptionsEnabled = storeOffersSubscriptions(storeSettings);
  const isCouponsEnabled = storeHasCoupons(storeSettings);
  const [orderType, setOrderType] = useState('one_time');
  const [subFrequency, setSubFrequency] = useState('daily');
  const [subStartDate, setSubStartDate] = useState(addDays(new Date(), 1));
  const [subCalendarOpen, setSubCalendarOpen] = useState(false);
  // "Subscription" turns every cart item into its own subscription, so every
  // item has to be one the store offers on subscription. The cart doesn't
  // carry that flag, and it can change, so it is read from the store's current
  // catalogue: when Subscription is picked (to say which items are in the way)
  // and again on Start Subscription, before anything is created. The server
  // refuses a non-eligible product regardless; this keeps the cart from
  // starting some subscriptions and then failing on another item.
  const [subscriptionCatalogue, setSubscriptionCatalogue] = useState(null);
  const loadSubscriptionCatalogue = async () => {
    const res = await axios.get(`${API}/customer/products`);
    setSubscriptionCatalogue(res.data);
    return res.data;
  };
  useEffect(() => {
    if (orderType === 'subscription') loadSubscriptionCatalogue().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType]);
  const notSubscribableNote = orderType === 'subscription' && subscriptionCatalogue
    ? notSubscribableMessage(cartItemsNotSubscribable(items, subscriptionCatalogue))
    : null;

  useEffect(() => {
    loadDeliverySlots();
    loadAvailableCoupons();
    if (user?.admin_id && storeSettings?.store_pickup_enabled) {
      loadPickupBranches();
    }
    // Auto-set today's date for instant delivery mode
    const instantMode = storeSettings?.regular_order_mode === 'instant' || !storeSettings?.regular_order_mode;
    if (instantMode && !deliveryDate) {
      setDeliveryDate(new Date());
    }
    // Apartments must be loaded BEFORE picking the default delivery address (not
    // just fired in parallel) — otherwise the very first selection would always
    // fall back to the raw default, racing ahead of the data it needs to decide
    // whether that default actually has a valid serviceable apartment.
    (async () => {
      let apts = [];
      if (isApartmentsEnabled) {
        try {
          const res = await axios.get(`${API}/admin/serviceable-apartments`);
          apts = res.data || [];
          setApartments(apts);
        } catch { /* ignore */ }
      }
      loadDeliveryAddresses(apts);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // An address "has a valid serviceable apartment" if its pincode matches a
  // configured apartment (or an apartment left pincode-less, per the backend's
  // matching rule) AND its apartment_id is one of those matches.
  const addressHasValidApartment = (addr, apts) => {
    if (!addr) return false;
    const matching = apts.filter(a => !a.pincode || a.pincode === addr.pincode);
    return matching.length > 0 && matching.some(a => a.id === addr.apartment_id);
  };

  // Picks which address checkout should default to.
  // - Gating inactive (feature off, or on but nothing configured yet): unchanged
  //   behavior — the customer's flagged default, or the first saved address.
  // - Gating active: prefer the flagged default IF it has a valid serviceable
  //   apartment; otherwise fall through to the first OTHER address that does;
  //   only if none qualify do we fall back to the default/first (so
  //   apartmentIssue has something concrete to explain and let the customer fix).
  const pickDefaultAddress = (addrs, apts) => {
    const flaggedDefault = addrs.find(a => a.is_default) || null;
    const gatingActive = isApartmentsEnabled && apts.length > 0;
    if (!gatingActive) return flaggedDefault || addrs[0] || null;
    if (addressHasValidApartment(flaggedDefault, apts)) return flaggedDefault;
    const validAlternative = addrs.find(a => addressHasValidApartment(a, apts));
    return validAlternative || flaggedDefault || addrs[0] || null;
  };

  // Gating is only active once the feature is on AND the admin has actually
  // configured at least one apartment — enabled-but-unconfigured must behave
  // like the feature doesn't exist at all, not block every order.
  const apartmentGatingActive = isApartmentsEnabled && apartments.length > 0;

  // Mirrors the backend's _validate_and_enrich_apartment matching rule: apartments
  // left without a pincode are treated as serviceable for any PIN. Re-checks
  // whichever address is currently selected — including older saved addresses
  // that predate this feature or an apartment the admin has since removed.
  const apartmentIssue = (() => {
    if (!apartmentGatingActive || fulfillmentType !== 'delivery' || !selectedDeliveryAddr) return null;
    const matching = apartments.filter(a => !a.pincode || a.pincode === selectedDeliveryAddr.pincode);
    if (matching.length === 0) return 'This delivery location is currently not serviceable. Please change your delivery address.';
    if (!matching.some(a => a.id === selectedDeliveryAddr.apartment_id)) {
      return 'Please select a serviceable apartment for this delivery address.';
    }
    return null;
  })();

  const loadDeliverySlots = async () => {
    try {
      const response = await axios.get(`${API}/admin/delivery-slots`);
      setDeliverySlots(response.data);
      if (response.data.length > 0 && !deliverySlot) {
        setDeliverySlot(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to load delivery slots:', error);
    }
  };

  // aptsOverride lets the mount effect pass a freshly-fetched apartments list
  // directly, avoiding a stale-closure read of `apartments` state before its
  // own setState has flushed. Calls elsewhere (e.g. after adding a new address)
  // omit it and fall back to `apartments` state, which by then is already loaded.
  const loadDeliveryAddresses = async (aptsOverride) => {
    try {
      const res = await axios.get(`${API}/customer/addresses`);
      const addrs = res.data || [];
      setDeliveryAddresses(addrs);
      const apts = aptsOverride !== undefined ? aptsOverride : apartments;
      const best = pickDefaultAddress(addrs, apts);
      if (best) setSelectedDeliveryAddr(best);
    } catch { /* silently fail */ }
  };


  const loadPickupBranches = async () => {
    try {
      const res = await axios.get(`${API}/store/${user.admin_id}/pickup-branches`);
      setPickupBranches(res.data);
      if (res.data.length > 0) setSelectedBranch(res.data[0]);
    } catch (e) {
      console.error('Failed to load pickup branches:', e);
    }
  };

  const loadAvailableCoupons = async () => {
    try {
      const res = await axios.get(`${API}/customer/coupons`);
      setAvailableCoupons(res.data);
    } catch { /* ignore */ }
  };

  // Check serviceability & delivery fee when address or cart total changes
  useEffect(() => {
    if (fulfillmentType !== 'delivery' || !selectedDeliveryAddr) {
      setDeliveryFee(0); setNotServiceable(false); setServiceMessage(null); setDistanceKm(null); setMinOrderVal(null); setFreeDeliveryEligible(false);
      return;
    }
    const checkDelivery = async () => {
      try {
        const res = await axios.post(`${API}/customer/check-delivery`, {
          latitude: selectedDeliveryAddr.latitude,
          longitude: selectedDeliveryAddr.longitude,
          pincode: selectedDeliveryAddr.pincode || '',
          cart_total: totalAmount,
        });
        setDeliveryFee(res.data.delivery_fee || 0);
        setNotServiceable(!res.data.serviceable);
        setServiceMessage(res.data.message || null);
        setDistanceKm(res.data.distance_km);
        setMinOrderVal(res.data.min_order_value || null);
        setFreeDeliveryEligible(res.data.free_delivery_eligible || false);
      } catch {
        setDeliveryFee(0); setNotServiceable(false); setServiceMessage(null); setMinOrderVal(null);
      }
    };
    checkDelivery();
  }, [selectedDeliveryAddr, fulfillmentType, totalAmount]);

  const handleApplyCoupon = async (codeOverride) => {
    // The Apply button next to the code box passes its click event here, not a
    // code — only an offer's own Apply button passes one.
    const code = typeof codeOverride === 'string' ? codeOverride : couponCode;
    if (!code.trim()) return;
    setApplyingCoupon(true);
    try {
      const res = await axios.post(`${API}/customer/coupons/apply?code=${code.toUpperCase()}&cart_total=${totalAmount}`);
      setAppliedCoupon(res.data);
      setCouponCode(code.toUpperCase());
      toast.success(`Coupon "${res.data.code}" applied!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid coupon');
      setAppliedCoupon(null);
    } finally { setApplyingCoupon(false); }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  // ---- Server-authoritative pricing ----
  // Cart prices are captured when an item is added and go stale when the store
  // changes a price, while the server prices every order itself. So the
  // checkout shows the server's figures — and refreshes the cart's prices from
  // them — making the total displayed the total charged or collected. The
  // cart's own sum is only a fallback while a quote loads (or if it fails).
  const orderAddress = fulfillmentType === 'store_pickup' ? null : (selectedDeliveryAddr || user?.address || null);
  const quoteKey = cartQuoteKey(items, appliedCoupon?.code, fulfillmentType, orderAddress);
  const [quote, setQuote] = useState(null);

  const applyQuote = useCallback((serverQuote, key) => {
    setQuote({ ...serverQuote, key });
    repriceItems(serverQuote.lines || []);
  }, [repriceItems]);

  useEffect(() => {
    if (user?.role !== 'customer' || items.length === 0) { setQuote(null); return undefined; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/customer/cart/quote`, {
          items: items.map(i => ({
            product_id: i.product_id, product_name: i.product_name,
            variant_label: i.variant_label || null, quantity: i.quantity,
          })),
          coupon_code: appliedCoupon?.code || null,
          fulfillment_type: fulfillmentType,
          delivery_address: orderAddress,
        });
        if (!cancelled) applyQuote(res.data, quoteKey);
      } catch {
        // e.g. an item became unavailable — show the cart's own figures; placing
        // the order reports the real reason.
        if (!cancelled) setQuote(null);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
    // quoteKey already captures every input used here (items, coupon,
    // fulfilment, address) but deliberately not prices, so a refresh that
    // updates prices doesn't trigger another quote.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteKey, user?.role]);

  const quoteCurrent = !!quote && quote.key === quoteKey;
  const couponDiscount = quoteCurrent ? quote.coupon_discount : (appliedCoupon?.discount || 0);
  const effectiveDeliveryFee = quoteCurrent
    ? quote.delivery_fee
    : ((appliedCoupon?.free_delivery || fulfillmentType === 'store_pickup') ? 0 : deliveryFee);
  const finalAmount = quoteCurrent
    ? quote.payable_amount
    : Math.max(0, totalAmount - couponDiscount + effectiveDeliveryFee);

  // The server refused the order because the total displayed was not what it
  // computed (e.g. a price changed after the cart was opened). Nothing was
  // created or charged; show the correct figures so the customer can confirm.
  const handlePriceChanged = (error) => {
    const changed = priceChangedQuote(error);
    if (!changed) return false;
    applyQuote(changed.quote, quoteKey);
    toast.warning(changed.message || 'Prices have changed. Please review the updated total.');
    return true;
  };

  // After an order is genuinely placed, offer browser notifications once (web
  // only; native apps use FCM). The permission prompt itself only comes from
  // the Enable tap, never automatically — see webPush.js.
  const offerOrderUpdates = () => {
    if (!user?.id || !shouldOfferCustomerWebPush()) return;
    markCustomerWebPushOffered();
    toast('Get updates on your order?', {
      description: 'Turn on notifications to hear when it is confirmed and delivered.',
      duration: 12000,
      action: {
        label: 'Enable',
        onClick: () => {
          enableCustomerWebPush(user.id).then((ok) => {
            if (ok) toast.success('Order notifications turned on');
          });
        },
      },
    });
  };

  const handlePlaceOrder = async () => {
    // Min order check
    if (minOrderVal && totalAmount < minOrderVal) {
      toast.error(`Minimum order value is ${currencySymbol}${minOrderVal}. Add ${currencySymbol}${(minOrderVal - totalAmount).toFixed(0)} more.`);
      return;
    }
    if (fulfillmentType === 'delivery') {
      if (notServiceable) {
        toast.error(serviceMessage || 'Delivery is not available for your selected address');
        return;
      }
      if (apartmentIssue) {
        toast.error(apartmentIssue);
        return;
      }
      if (!isInstantDelivery && !deliverySlot) {
        toast.error('Please select a delivery slot');
        return;
      }
    }
    if (!deliveryDate) {
      toast.error(fulfillmentType === 'store_pickup' ? 'Please select a pickup date' : 'Please select a delivery date');
      return;
    }

    setLoading(true);
    try {
      if (orderType === 'subscription') {
        // Nothing is created unless every item can be subscribed to.
        const blocked = notSubscribableMessage(cartItemsNotSubscribable(items, await loadSubscriptionCatalogue()));
        if (blocked) {
          toast.error(blocked);
          return;
        }
        // Create individual subscriptions for each cart item
        for (const item of items) {
          await axios.post(`${API}/customer/subscriptions`, {
            product_id: item.product_id,
            quantity: item.quantity,
            frequency: subFrequency,
            delivery_slot_id: deliverySlot?.id || '',
            start_date: format(subStartDate, 'yyyy-MM-dd'),
            payment_mode: paymentMode === 'cash' ? 'postpaid' : 'prepaid',
          });
        }
        toast.success('Subscription created successfully!');
        clearCart();
        navigate('/subscriptions');
      } else {
        const orderData = {
          order_type: 'one_time',
          items: items.map(item => ({
            product_id: item.product_id,
            product_name: item.product_name,
            quantity: item.quantity,
            price: item.price,
            total: item.total,
            variant_label: item.variant_label || null,
            unit: item.variant_label || item.unit || null,
          })),
          delivery_slot_id: fulfillmentType === 'store_pickup' ? '' : (deliverySlot?.id || ''),
          delivery_date: format(deliveryDate || new Date(), 'yyyy-MM-dd'),
          payment_mode: paymentMode,
          delivery_address: fulfillmentType === 'store_pickup' ? null : (selectedDeliveryAddr || user?.address || null),
          coupon_code: appliedCoupon?.code || null,
          coupon_discount: couponDiscount,
          delivery_fee: effectiveDeliveryFee,
          fulfillment_type: fulfillmentType,
          // The total shown on screen. The server never uses it as the amount —
          // it prices the order itself and answers 409 PRICE_CHANGED if this
          // differs, so the customer is never charged a total they didn't see.
          expected_payable: finalAmount,
        };

        if (paymentMode === 'online') {
          // --- Razorpay Payment Flow ---
          try {
            // 1. Create the order in our backend first (status: pending)
            const orderRes = await axios.post(`${API}/customer/orders`, orderData);
            const createdOrder = orderRes.data;
            const finalAmount = (createdOrder.total_amount || totalAmount) + (effectiveDeliveryFee || 0) - (couponDiscount || 0);

            // 2. Create Razorpay order
            const rzpRes = await axios.post(`${API}/payment/create-order`, {
              amount: Math.max(finalAmount, 1),
              currency: 'INR',
              order_id: createdOrder.id,
              receipt: createdOrder.order_number,
            });

            // 3. Open Razorpay Checkout
            const options = {
              key: rzpRes.data.key_id,
              amount: rzpRes.data.amount,
              currency: rzpRes.data.currency,
              order_id: rzpRes.data.razorpay_order_id,
              name: storeSettings?.store_name || 'Store',
              description: `Order #${createdOrder.order_number}`,
              prefill: {
                name: user?.name || '',
                contact: user?.phone || '',
                email: user?.email || '',
              },
              theme: { color: getComputedStyle(document.documentElement).getPropertyValue('--theme-primary').trim() || '#4CAF50' },
              handler: async (response) => {
                // 4. Verify payment on backend
                try {
                  await axios.post(`${API}/payment/verify`, {
                    razorpay_order_id: response.razorpay_order_id,
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature: response.razorpay_signature,
                    order_id: createdOrder.id,
                  });
                  toast.success('Payment successful! Order placed.');
                  offerOrderUpdates();
                  clearCart();
                  navigate('/orders');
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Payment verification failed. Contact support.');
                }
              },
              modal: {
                ondismiss: () => {
                  // The order stays in awaiting_payment: not placed, not shown to
                  // the store, and not announced — but kept so payment can be
                  // completed later rather than losing the basket.
                  toast.info('Payment cancelled — your order has not been placed yet.');
                  setLoading(false);
                },
              },
            };

            // Load Razorpay checkout script dynamically (no npm package needed)
            const loadRazorpay = () => new Promise((resolve, reject) => {
              if (window.Razorpay) return resolve(window.Razorpay);
              const script = document.createElement('script');
              script.src = 'https://checkout.razorpay.com/v1/checkout.js';
              script.onload = () => resolve(window.Razorpay);
              script.onerror = () => reject(new Error('Failed to load Razorpay'));
              document.body.appendChild(script);
            });

            const Razorpay = await loadRazorpay();
            const rzp = new Razorpay(options);
            rzp.on('payment.failed', (response) => {
              toast.error(`Payment failed: ${response.error.description}`);
              setLoading(false);
            });
            patchRazorpayFrameSafeArea();
            rzp.open();
            return; // Don't set loading to false yet — Razorpay modal is open
          } catch (error) {
            // A PRICE_CHANGED refusal happens before any order or Razorpay order
            // exists, so Razorpay never opens on a total the customer didn't see.
            if (!handlePriceChanged(error)) {
              toast.error(error.response?.data?.detail || 'Failed to initiate payment');
            }
            setLoading(false);
            return;
          }
        }

        // --- Cash on Delivery / Wallet ---
        await axios.post(`${API}/customer/orders`, orderData);
        toast.success('Order placed successfully!');
        offerOrderUpdates();
        clearCart();
        navigate('/orders');
      }
    } catch (error) {
      if (!handlePriceChanged(error)) {
        toast.error(error.response?.data?.detail || 'Failed to place order');
      }
    } finally {
      setLoading(false);
    }
  };

  const currencySymbol = storeSettings?.currency_symbol || '₹';
  const isInstantDelivery = storeSettings?.regular_order_mode === 'instant' || !storeSettings?.regular_order_mode;
  // Strip time from minDate so today's midnight (00:00) is NOT considered "before" now.
  const today = new Date(); today.setHours(0, 0, 0, 0);
  // Slot-based delivery now allows today too — individual already-started slots
  // are filtered out of the slot picker below (and re-checked server-side),
  // rather than blanket-disallowing the whole day.
  const minDate = today;
  const isStorePickupEnabled = storeSettings?.store_pickup_enabled && storeSettings?.tenant_features?.store_pickup !== false;

  // A slot is only selectable on a given date once both its cut-off time
  // (or, if unset, its own start time) AND its cut_off_days lead time — how
  // many days in advance it must be ordered, 0 = same day — haven't passed
  // yet. This is now the ONLY place "when does ordering close" is decided:
  // there used to also be a separate tenant-wide day-level cut-off (Admin
  // Settings' regular_cut_off_time/regular_cut_off_days) that this picker
  // never checked, so a slot the picker showed as open could still be
  // rejected by that other rule on submit ("Failed to place order" with no
  // visible reason). Cut-off configuration now lives entirely on each slot,
  // so frontend and backend (POST /customer/orders) always agree.
  const isSlotAvailableOnDate = (slot, date) => {
    if (!date || !slot?.start_time) return true;
    const cutOff = slot.cut_off_time || slot.start_time;
    const [h, m] = cutOff.split(':').map(Number);
    const slotCutOff = new Date(date);
    slotCutOff.setHours(h, m || 0, 0, 0);
    slotCutOff.setDate(slotCutOff.getDate() + (slot.cut_off_days ?? 0));
    return new Date() < slotCutOff;
  };
  const isSlotAvailable = (slot) => isSlotAvailableOnDate(slot, deliveryDate);
  const availableDeliverySlots = deliverySlots.filter(isSlotAvailable);

  // A calendar date is only selectable if at least one configured slot could
  // still be ordered for it — otherwise picking it would just lead to an
  // empty slot list. No-op for instant delivery (no slots involved) and for
  // a slot-based tenant with zero slots configured yet (nothing to check
  // against — the "no delivery slots available" state elsewhere covers that).
  const isDateOrderable = (date) => (
    isInstantDelivery || deliverySlots.length === 0 || deliverySlots.some(slot => isSlotAvailableOnDate(slot, date))
  );

  // If the chosen date/time makes the previously-selected slot no longer
  // available (e.g. customer picked today after already picking a slot,
  // or simply waited past its cut-off), clear it so they must re-pick.
  useEffect(() => {
    if (deliverySlot && !isSlotAvailable(deliverySlot)) {
      setDeliverySlot(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryDate, deliverySlots]);

  // Same idea for the date itself — if time passes while the cart is open
  // and every slot's cut-off for the chosen date lapses, clear it too rather
  // than let a now-stale date sit selected through to checkout.
  useEffect(() => {
    if (deliveryDate && !isDateOrderable(deliveryDate)) {
      setDeliveryDate(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryDate, deliverySlots]);

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (items.length === 0) {
    return (
      <CustomerLayout showHeader={false} showBottomNav={false}>
        <header className="sticky top-0 z-40 glass border-b border-black/5">
          <div className="flex items-center gap-4 px-4 py-3">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2" data-testid="back-btn">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="font-heading font-bold text-lg text-[var(--theme-text-main)]">Cart</h1>
          </div>
        </header>
        <div className="min-h-[60vh] flex flex-col items-center justify-center p-6">
          <ShoppingBag className="w-24 h-24 text-[var(--theme-text-muted)] opacity-30 mb-4" />
          <h2 className="font-heading text-xl font-bold text-[var(--theme-text-main)] mb-2">
            Your cart is empty
          </h2>
          <p className="text-[var(--theme-text-muted)] text-center mb-6">
            Add some fresh products to get started!
          </p>
          <Button
            onClick={() => navigate('/products')}
            className="bg-[var(--theme-primary)] text-white rounded-full px-8"
            data-testid="browse-products-btn"
          >
            Browse Products
          </Button>
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout showHeader={false} showBottomNav={false}>
      <div className="min-h-screen bg-[var(--theme-background)]">
        {/* Header */}
        <header className="sticky top-0 z-40 glass border-b border-black/5">
          <div className="flex items-center gap-4 px-4 py-3">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2" data-testid="back-btn">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="font-heading font-bold text-lg text-[var(--theme-text-main)]">
              Your Cart ({items.length} items)
            </h1>
          </div>
        </header>

        <div className="p-4 pb-32 space-y-4">
          {/* Cart Items */}
          <div className="bg-[var(--theme-surface)] rounded-2xl border border-black/5 divide-y divide-black/5">
            {items.map((item) => {
              const itemKey = item.cart_key || item.product_id;
              return (
              <div key={itemKey} className="p-4 flex gap-4" data-testid={`cart-item-${item.product_id}`}>
                <div className="w-20 h-20 bg-gray-50 rounded-xl overflow-hidden flex-shrink-0">
                  {item.product_image ? (
                    <img src={item.product_image} alt={item.product_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-2xl bg-[var(--theme-primary)]/10">
                      {item.product_name.charAt(0)}
                    </div>
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-[var(--theme-text-main)] line-clamp-2">{item.product_name}</h4>
                  <p className="text-xs text-[var(--theme-text-muted)] mb-2">
                    {item.variant_label ? `${item.variant_label}` : item.unit}
                  </p>
                  {item.promotion_name && (
                    <p className="inline-flex items-center gap-1 text-[11px] font-medium text-green-700 bg-green-50 border border-green-200 rounded-md px-1.5 py-0.5 mb-2 max-w-full" data-testid={`cart-promotion-${item.product_id}`}>
                      <BadgePercent className="w-3 h-3 shrink-0" aria-hidden="true" />
                      <span className="truncate">{item.promotion_label} · {item.promotion_name}</span>
                    </p>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-[var(--theme-text-main)]">
                      {currencySymbol}{item.total}
                    </span>
                    
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => removeItem(itemKey)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                        data-testid={`remove-${item.product_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      
                      <div className="flex items-center gap-2 bg-[var(--theme-primary)] rounded-full">
                        <button
                          onClick={() => updateQuantity(itemKey, item.quantity - 1)}
                          className="w-8 h-8 flex items-center justify-center text-white"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="text-white font-medium w-4 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(itemKey, item.quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center text-white"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          {/* Delivery Options */}
          <div className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-black/5 space-y-4">
            <h3 className="font-heading font-bold text-[var(--theme-text-main)]">Order Options</h3>
            
            {/* Fulfillment Type Toggle */}
            {isStorePickupEnabled && (
              <div>
                <label className="text-sm font-medium text-[var(--theme-text-muted)] mb-2 block">
                  How would you like to get your order?
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => setFulfillmentType('delivery')}
                    className={`p-3 rounded-xl border-2 transition-all flex items-center gap-2 justify-center ${
                      fulfillmentType === 'delivery'
                        ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)]/5'
                        : 'border-black/5 hover:border-[var(--theme-primary)]/50'
                    }`}
                    data-testid="fulfillment-delivery"
                  >
                    <Truck className={`w-4 h-4 ${fulfillmentType === 'delivery' ? 'text-[var(--theme-primary)]' : 'text-[var(--theme-text-muted)]'}`} />
                    <span className={`font-medium text-sm ${
                      fulfillmentType === 'delivery' ? 'text-[var(--theme-primary)]' : 'text-[var(--theme-text-main)]'
                    }`}>Home Delivery</span>
                  </button>
                  <button
                    onClick={() => setFulfillmentType('store_pickup')}
                    className={`p-3 rounded-xl border-2 transition-all flex items-center gap-2 justify-center ${
                      fulfillmentType === 'store_pickup'
                        ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)]/5'
                        : 'border-black/5 hover:border-[var(--theme-primary)]/50'
                    }`}
                    data-testid="fulfillment-pickup"
                  >
                    <Store className={`w-4 h-4 ${fulfillmentType === 'store_pickup' ? 'text-[var(--theme-primary)]' : 'text-[var(--theme-text-muted)]'}`} />
                    <span className={`font-medium text-sm ${
                      fulfillmentType === 'store_pickup' ? 'text-[var(--theme-primary)]' : 'text-[var(--theme-text-main)]'
                    }`}>Store Pickup</span>
                  </button>
                </div>
              </div>
            )}

            {/* Delivery Address Selection */}
            {fulfillmentType === 'delivery' && (
              <div>
                <label className="text-sm font-medium text-[var(--theme-text-muted)] mb-2 block">
                  Delivery Address
                </label>
                {selectedDeliveryAddr ? (
                  <div className="border border-[var(--theme-primary)]/20 bg-[var(--theme-primary)]/5 rounded-xl p-3">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-[var(--theme-primary)] mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-sm">{selectedDeliveryAddr.label}</span>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{selectedDeliveryAddr.full_address}</p>
                      </div>
                      <button
                        onClick={() => setSelectedDeliveryAddr(null)}
                        className="text-xs text-[var(--theme-primary)] font-medium shrink-0"
                        data-testid="change-address-btn"
                      >
                        Change
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-amber-300 bg-amber-50 rounded-xl p-4 text-center" data-testid="no-address-warning">
                    <MapPin className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                    <p className="text-sm text-amber-700 font-medium mb-2">No delivery address selected</p>
                    {deliveryAddresses.length > 0 ? (
                      <div className="space-y-2">
                        {deliveryAddresses.map(addr => (
                          <button
                            key={addr.id}
                            onClick={() => setSelectedDeliveryAddr(addr)}
                            className="w-full flex items-center gap-2 p-2 text-left bg-white rounded-lg border border-gray-100 hover:border-[var(--theme-primary)]/50"
                            data-testid={`pick-addr-${addr.id}`}
                          >
                            <Check className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                            <div className="min-w-0">
                              <span className="text-xs font-medium">{addr.label}</span>
                              <p className="text-xs text-gray-400 truncate">{addr.full_address}</p>
                            </div>
                          </button>
                        ))}
                        <button
                          onClick={() => setShowCartMapPicker(true)}
                          className="text-xs text-[var(--theme-primary)] font-medium"
                          data-testid="add-address-checkout-btn"
                        >
                          + Add New Address
                        </button>
                      </div>
                    ) : (
                      <Button
                        onClick={() => setShowCartMapPicker(true)}
                        size="sm"
                        className="bg-[var(--theme-primary)] text-white"
                        data-testid="add-first-address-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add Delivery Address
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}


            {/* Date Picker — hidden for instant delivery */}
            {!isInstantDelivery && (
            <div>
              <label className="text-sm font-medium text-[var(--theme-text-muted)] mb-2 block">
                {fulfillmentType === 'store_pickup' ? 'Pickup Date' : 'Delivery Date'}
              </label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left h-12 border-black/10"
                    data-testid="delivery-date-btn"
                  >
                    <CalendarDays className="mr-2 h-4 w-4" />
                    {deliveryDate ? format(deliveryDate, 'EEE, dd MMM yyyy') : (fulfillmentType === 'store_pickup' ? 'Select pickup date' : 'Select delivery date')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={deliveryDate}
                    onSelect={(date) => {
                      setDeliveryDate(date);
                      setCalendarOpen(false);
                    }}
                    disabled={(date) => date < minDate || !isDateOrderable(date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            )}

            {/* Delivery Slot - only for home delivery + slot-based mode */}
            {fulfillmentType === 'delivery' && !isInstantDelivery && (
              <div>
                <label className="text-sm font-medium text-[var(--theme-text-muted)] mb-2 block">
                  Delivery Slot
                </label>
                <Select
                  value={deliverySlot?.id || ''}
                  onValueChange={(value) => {
                    const slot = availableDeliverySlots.find(s => s.id === value);
                    setDeliverySlot(slot);
                  }}
                >
                  <SelectTrigger className="h-12" data-testid="delivery-slot-select">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <SelectValue placeholder="Select delivery slot" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {availableDeliverySlots.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-[var(--theme-text-muted)]">No slots left for today — pick a later date</div>
                    ) : (
                      availableDeliverySlots.map((slot) => (
                        <SelectItem key={slot.id} value={slot.id}>
                          <span className="block">{slot.name} ({formatTime(slot.start_time)} - {formatTime(slot.end_time)})</span>
                          <span className="block text-xs text-muted-foreground">
                            Cut-off {formatTime(slot.cut_off_time || slot.start_time)}
                          </span>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Store Pickup Info */}
            {fulfillmentType === 'store_pickup' && (
              <div className="space-y-3" data-testid="pickup-info">
                <label className="text-sm font-medium text-[var(--theme-text-muted)] block">
                  Select pickup location
                </label>
                {pickupBranches.length > 0 ? (
                  <div className="space-y-2">
                    {pickupBranches.map((branch) => (
                      <button
                        key={branch.id}
                        onClick={() => setSelectedBranch(branch)}
                        className={`w-full text-left rounded-xl border-2 p-3 transition-all ${
                          selectedBranch?.id === branch.id
                            ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)]/5'
                            : 'border-black/5 hover:border-[var(--theme-primary)]/30'
                        }`}
                        data-testid={`pickup-branch-${branch.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <Store className="w-5 h-5 mt-0.5 text-[var(--theme-primary)] shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm text-[var(--theme-text-main)]">{branch.name}</p>
                            <p className="text-xs text-[var(--theme-text-muted)] mt-0.5">{branch.address}{branch.city ? `, ${branch.city}` : ''}</p>
                            {branch.opening_hours && (
                              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {branch.opening_hours}
                              </p>
                            )}
                            {branch.phone && (
                              <p className="text-xs text-blue-600 mt-0.5">{branch.phone}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="bg-green-50 rounded-xl p-3 border border-green-200">
                    <div className="flex items-center gap-2 text-green-800">
                      <Store className="w-4 h-4 shrink-0" />
                      <div>
                        <p className="text-sm font-medium">Pick up from store</p>
                        <p className="text-xs text-green-600 mt-0.5">Your order will be ready for pickup on the selected date</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Payment Mode */}
            <div>
              <label className="text-sm font-medium text-[var(--theme-text-muted)] mb-2 block">
                Payment Mode
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {['cash', 'online'].map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setPaymentMode(mode)}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      paymentMode === mode
                        ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)]/5'
                        : 'border-black/5 hover:border-[var(--theme-primary)]/50'
                    }`}
                    data-testid={`payment-${mode}`}
                  >
                    <span className={`font-medium capitalize ${
                      paymentMode === mode ? 'text-[var(--theme-primary)]' : 'text-[var(--theme-text-main)]'
                    }`}>
                      {mode === 'cash' ? 'Cash on Delivery' : 'Pay Online'}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Order Type — One-Time vs Subscription */}
            {isSubscriptionsEnabled && (
              <div>
                <label className="text-sm font-medium text-[var(--theme-text-muted)] mb-2 block">
                  Order Type
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setOrderType('one_time')}
                    className={`p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                      orderType === 'one_time'
                        ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)]/5'
                        : 'border-black/5 hover:border-[var(--theme-primary)]/50'
                    }`}
                    data-testid="order-type-onetime"
                  >
                    <ShoppingBag className="w-4 h-4" />
                    <span className={`font-medium ${
                      orderType === 'one_time' ? 'text-[var(--theme-primary)]' : 'text-[var(--theme-text-main)]'
                    }`}>One-Time</span>
                  </button>
                  <button
                    onClick={() => setOrderType('subscription')}
                    className={`p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                      orderType === 'subscription'
                        ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)]/5'
                        : 'border-black/5 hover:border-[var(--theme-primary)]/50'
                    }`}
                    data-testid="order-type-subscription"
                  >
                    <RefreshCcw className="w-4 h-4" />
                    <span className={`font-medium ${
                      orderType === 'subscription' ? 'text-[var(--theme-primary)]' : 'text-[var(--theme-text-main)]'
                    }`}>Subscribe</span>
                  </button>
                </div>

                {/* Subscription Config */}
                {orderType === 'subscription' && (
                  <div className="mt-4 space-y-4 bg-[var(--theme-primary)]/5 rounded-xl p-4 border border-[var(--theme-primary)]/20">
                    <div>
                      <label className="text-sm font-medium text-[var(--theme-text-main)] mb-2 block">
                        Delivery Frequency
                      </label>
                      <Select value={subFrequency} onValueChange={setSubFrequency}>
                        <SelectTrigger className="h-12" data-testid="sub-frequency-select">
                          <div className="flex items-center gap-2">
                            <RefreshCcw className="w-4 h-4" />
                            <SelectValue />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          {FREQUENCY_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[var(--theme-text-main)] mb-2 block">
                        Start Date
                      </label>
                      <Popover open={subCalendarOpen} onOpenChange={setSubCalendarOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left h-12 border-black/10"
                            data-testid="sub-start-date-btn"
                          >
                            <CalendarDays className="mr-2 h-4 w-4" />
                            {format(subStartDate, 'EEE, dd MMM yyyy')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={subStartDate}
                            onSelect={(date) => { if (date) { setSubStartDate(date); setSubCalendarOpen(false); } }}
                            disabled={date => { const tmr = addDays(new Date(), 1); tmr.setHours(0,0,0,0); return date < tmr; }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Delivery Slot for subscription */}
                    {deliverySlots.length > 0 && (
                      <div>
                        <label className="text-sm font-medium text-[var(--theme-text-main)] mb-2 block">
                          Delivery Slot
                        </label>
                        <Select
                          value={deliverySlot?.id || ''}
                          onValueChange={(value) => {
                            const slot = deliverySlots.find(s => s.id === value);
                            setDeliverySlot(slot);
                          }}
                        >
                          <SelectTrigger className="h-12" data-testid="sub-delivery-slot">
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              <SelectValue placeholder="Select delivery slot" />
                            </div>
                          </SelectTrigger>
                          <SelectContent>
                            {deliverySlots.map(slot => (
                              <SelectItem key={slot.id} value={slot.id}>
                                <span className="block">{slot.name} {slot.start_time ? `(${formatTime(slot.start_time)} - ${formatTime(slot.end_time)})` : ''}</span>
                                <span className="block text-xs text-muted-foreground">
                                  Cut-off {formatTime(slot.cut_off_time || slot.start_time)}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <p className="text-xs text-[var(--theme-text-muted)]">
                      Each item in your cart will be subscribed with the above frequency. You can manage subscriptions from your profile.
                    </p>
                    {items.some(i => i.promotion_name) && (
                      <p className="text-xs text-amber-700" data-testid="sub-promotion-note">
                        Promotions apply to one-time orders only — subscriptions are charged regular prices.
                      </p>
                    )}
                    {notSubscribableNote && (
                      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="sub-not-eligible">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{notSubscribableNote}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Coupon Code — only where the store offers coupons (the server refuses them otherwise) */}
          {isCouponsEnabled && (
          <div className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-black/5" data-testid="coupon-section">
            <h3 className="font-heading font-bold text-[var(--theme-text-main)] mb-3 flex items-center gap-2">
              <Tag className="w-4 h-4" /> Apply Coupon
            </h3>
            {appliedCoupon ? (
              <div className="flex items-center justify-between bg-green-50 rounded-xl p-3">
                <div>
                  <p className="text-sm font-bold text-green-700">{appliedCoupon.code}</p>
                  <p className="text-xs text-green-600">{appliedCoupon.title} — You save {currencySymbol}{couponDiscount.toFixed(2)}</p>
                </div>
                <button onClick={removeCoupon} className="p-1.5 hover:bg-green-100 rounded-lg" data-testid="remove-coupon-btn">
                  <X className="w-4 h-4 text-green-700" />
                </button>
              </div>
            ) : (
              <>
                <div className="flex gap-2 mb-3">
                  <input
                    value={couponCode}
                    onChange={e => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter coupon code"
                    className="flex-1 border border-black/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]"
                    data-testid="coupon-code-input"
                  />
                  <Button
                    onClick={handleApplyCoupon}
                    disabled={applyingCoupon || !couponCode.trim()}
                    className="bg-[var(--theme-primary)] text-white rounded-xl px-5"
                    data-testid="apply-coupon-btn"
                  >
                    {applyingCoupon ? '...' : 'Apply'}
                  </Button>
                </div>

                {/* Available Coupons */}
                {availableCoupons.length > 0 && (
                  <div className="space-y-2" data-testid="available-coupons">
                    <p className="text-xs font-medium text-[var(--theme-text-muted)]">Available Offers</p>
                    {availableCoupons.map(c => {
                      const minOrder = c.min_order_value || 0;
                      const canApply = totalAmount >= minOrder;
                      const shortBy = minOrder > 0 ? Math.max(0, minOrder - totalAmount) : 0;
                      const discountText = c.offer_type === 'percent_discount'
                        ? `${c.discount_value}% off${c.max_discount ? ` (up to ${currencySymbol}${c.max_discount})` : ''}`
                        : c.offer_type === 'flat_discount'
                          ? `${currencySymbol}${c.discount_value} off`
                          : c.offer_type === 'free_delivery'
                            ? 'Free Delivery'
                            : c.title || 'Offer';

                      return (
                        <div key={c.id} className={`rounded-xl border p-3 transition-all ${canApply ? 'border-green-200 bg-green-50/50' : 'border-dashed border-gray-200 bg-gray-50/50'}`} data-testid={`coupon-offer-${c.code}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-xs font-bold tracking-wide bg-[var(--theme-primary)]/10 text-[var(--theme-primary)] px-2 py-0.5 rounded">{c.code}</span>
                                <span className="text-xs font-semibold text-gray-700">{discountText}</span>
                              </div>
                              {c.description && <p className="text-[11px] text-gray-500 mt-0.5">{c.description}</p>}
                              {!canApply && shortBy > 0 && (
                                <p className="text-[11px] text-amber-600 font-medium mt-1" data-testid={`coupon-nudge-${c.code}`}>
                                  Add {currencySymbol}{shortBy.toFixed(0)} more to avail this offer
                                  {minOrder > 0 && <span className="text-gray-400"> (min. {currencySymbol}{minOrder})</span>}
                                </p>
                              )}
                            </div>
                            {canApply && (
                              <button
                                onClick={() => handleApplyCoupon(c.code)}
                                className="text-xs font-bold text-[var(--theme-primary)] px-3 py-1.5 border border-[var(--theme-primary)] rounded-lg hover:bg-[var(--theme-primary)]/10 shrink-0"
                                data-testid={`apply-offer-${c.code}`}
                              >
                                Apply
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
          )}

          {/* Bill Summary */}
          <div className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-black/5">
            <h3 className="font-heading font-bold text-[var(--theme-text-main)] mb-4">Bill Summary</h3>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[var(--theme-text-muted)]">Item Total</span>
                <span className="font-medium">{currencySymbol}{(totalAmount + totalSavings).toFixed(2)}</span>
              </div>
              {totalSavings > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Product Savings</span>
                  <span className="font-medium">-{currencySymbol}{totalSavings.toFixed(2)}</span>
                </div>
              )}
              {quoteCurrent && quote.promotion_discount > 0 && (
                <div className="flex justify-between text-xs text-green-700 -mt-1" data-testid="bill-promotion-savings">
                  <span className="pl-3">incl. promotions</span>
                  <span>-{currencySymbol}{quote.promotion_discount.toFixed(2)}</span>
                </div>
              )}
              {couponDiscount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Coupon Discount ({appliedCoupon?.code})</span>
                  <span className="font-medium">-{currencySymbol}{couponDiscount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-[var(--theme-text-muted)]">Delivery Fee</span>
                <span className={`font-medium ${effectiveDeliveryFee === 0 ? 'text-green-600' : 'text-[var(--theme-text-main)]'}`}>
                  {appliedCoupon?.free_delivery ? 'FREE (coupon)' : effectiveDeliveryFee === 0 ? 'FREE' : `${currencySymbol}${effectiveDeliveryFee.toFixed(2)}`}
                </span>
              </div>
              {distanceKm != null && fulfillmentType === 'delivery' && (
                <p className="text-xs text-[var(--theme-text-muted)]">Distance: {distanceKm} km from store</p>
              )}
              {notServiceable && (
                <div className="flex items-start gap-2 p-3 bg-red-50 rounded-xl text-red-700 text-xs mt-2" data-testid="not-serviceable-warning">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{serviceMessage || 'Delivery is not available for your selected address.'}</span>
                </div>
              )}
              {!notServiceable && apartmentIssue && (
                <div className="flex items-start gap-2 p-3 bg-red-50 rounded-xl text-red-700 text-xs mt-2" data-testid="apartment-issue-warning">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{apartmentIssue} Tap "Change" above to pick or add a different address.</span>
                </div>
              )}
              {minOrderVal && totalAmount < minOrderVal && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-xl text-amber-700 text-xs mt-2" data-testid="min-order-warning">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Minimum order value is {currencySymbol}{minOrderVal}. Add {currencySymbol}{(minOrderVal - totalAmount).toFixed(0)} more to place your order.</span>
                </div>
              )}
              <div className="border-t border-black/5 pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="font-bold text-[var(--theme-text-main)]">To Pay</span>
                  <span className="font-bold text-lg text-[var(--theme-text-main)]">{currencySymbol}{finalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Bottom Bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-[var(--theme-surface)] border-t border-black/5 p-4 safe-area-bottom">
          <div className="flex items-center justify-between mb-3">
            <div>
              <span className="text-sm text-[var(--theme-text-muted)]">Total</span>
              <p className="font-heading font-bold text-xl text-[var(--theme-text-main)]">
                {currencySymbol}{finalAmount.toFixed(2)}
              </p>
            </div>
            <Button
              onClick={handlePlaceOrder}
              disabled={loading || (fulfillmentType === 'delivery' && !isInstantDelivery && !deliverySlot) || (!isInstantDelivery && !deliveryDate) || notServiceable || !!apartmentIssue || (minOrderVal && totalAmount < minOrderVal) || !!notSubscribableNote}
              className="bg-[var(--theme-primary)] text-white rounded-full px-8 h-12 text-lg font-semibold"
              data-testid="place-order-btn"
            >
              {notServiceable ? 'Not Serviceable' : (minOrderVal && totalAmount < minOrderVal) ? `Min. ${currencySymbol}${minOrderVal}` : loading ? 'Processing...' : orderType === 'subscription' ? 'Start Subscription' : 'Place Order'}
            </Button>
          </div>
        </div>
      </div>
      {/* Map Picker Overlay for Adding Address at Checkout */}
      {showCartMapPicker && (
        <div className="fixed inset-0 z-[9999] bg-white" data-testid="cart-map-picker">
          <MapAddressPicker
            initialAddress={cartDraftAddress || undefined}
            onSave={async (form) => {
              try {
                await axios.post(`${API}/customer/addresses`, { ...form, is_default: deliveryAddresses.length === 0 });
                toast.success('Address added!');
                setShowCartMapPicker(false);
                setCartDraftAddress(null);
                await loadDeliveryAddresses();
              } catch (error) {
                if (isSessionAuthError(error)) {
                  setShowCartMapPicker(false);
                  loginToContinue({ message: 'Please log in to add an address.', action: 'add-address', draft: form });
                  return;
                }
                toast.error(error.response?.data?.detail || 'Failed to save address');
              }
            }}
            onClose={() => { setShowCartMapPicker(false); setCartDraftAddress(null); }}
          />
        </div>
      )}
    </CustomerLayout>
  );
};

export default CustomerCart;
